import PropTypes from "prop-types";
import { useEffect, useState, useRef } from "react";
import clsx from "clsx";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import MenuCart from "../../components/header/sub-components/MenuCart";
import MobileMenu from "../../components/header/MobileMenu";
import { useGetUserProfileQuery } from "../../store/slices/user-slice";

const NAV_LOGO = process.env.PUBLIC_URL + "/assets/img/logo/navlogo.png";

/* Simplified Union Jack — avoids the watermarked flag asset. */
const UkFlag = () => (
  <svg className="c4nav-flag" viewBox="0 0 60 40" aria-hidden="true">
    <rect width="60" height="40" fill="#012169" />
    <path d="M0 0l60 40M60 0L0 40" stroke="#fff" strokeWidth="8" />
    <path d="M0 0l60 40M60 0L0 40" stroke="#C8102E" strokeWidth="4" clipPath="url(#c4uk)" />
    <path d="M30 0v40M0 20h60" stroke="#fff" strokeWidth="12" />
    <path d="M30 0v40M0 20h60" stroke="#C8102E" strokeWidth="7" />
  </svg>
);

const CATEGORIES = [
  "Organic Food",
  "Plants & Flowers",
  "Cosmetics",
  "Home & Furniture",
  "Handmade",
  "Pet Food",
];

const HeaderOne = ({ layout }) => {
  const navigate = useNavigate();
  const mainRef = useRef(null);
  const [stuck, setStuck] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { wishlistItems } = useSelector((state) => state.wishlist);
  const { cartItems } = useSelector((state) => state.cart);
  const { data, isLoading } = useGetUserProfileQuery();

  useEffect(() => {
    const offset = mainRef.current ? mainRef.current.offsetTop : 0;
    const onScroll = () => setStuck(window.scrollY > offset);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleCart = (e) => {
    e.currentTarget.nextSibling.classList.toggle("active");
  };

  const triggerMobileMenu = () => {
    const offcanvasMobileMenu = document.querySelector("#offcanvas-mobile-menu");
    if (offcanvasMobileMenu) offcanvasMobileMenu.classList.add("active");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    navigate("/shop-grid-standard");
  };

  const isLoggedIn = !!data?.user;
  const cartCount = cartItems && cartItems.length ? cartItems.length : 0;
  const wishlistCount = wishlistItems && wishlistItems.length ? wishlistItems.length : 0;
  const containerClass = layout === "container-fluid" ? "container-fluid" : "container";

  if (isLoading) {
    // Keep the black bar height stable while the profile query resolves.
    return <header className="c4nav" style={{ minHeight: 70 }} />;
  }

  return (
    <header className="c4nav header-area clearfix">
      {/* ---------------- Mobile bar ---------------- */}
      {/* Mirrors the mobile reference: menu, logo, then delivery / language
          / wishlist / account. Cart lives in the bottom bar, so it is not
          duplicated up here. */}
      <div className="c4nav-mobilebar">
        <button className="c4nav-hamburger" onClick={triggerMobileMenu} aria-label="Open menu">
          <i className="pe-7s-menu" />
        </button>

        <Link className="c4nav-logo" to={process.env.PUBLIC_URL + "/"}>
          <img src={NAV_LOGO} alt="CEY4HUB" />
        </Link>

        <div className="c4nav-mobilebar__spacer" />

        <div className="c4nav-mobilebar__deliver">
          <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6" />
          </svg>
          <span>
            <small>Deliver to</small>
            <strong>London</strong>
          </span>
        </div>

        <span className="c4nav-mobilebar__lang">EN/&pound;</span>

        <Link
          className="c4nav-mobilebar__icon"
          to={process.env.PUBLIC_URL + "/wishlist"}
          aria-label={`Wishlist, ${wishlistCount} item${wishlistCount === 1 ? "" : "s"}`}
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20.8 5.6a5 5 0 0 0-7.1 0L12 7.3l-1.7-1.7a5 5 0 0 0-7.1 7.1l8.8 8.8 8.8-8.8a5 5 0 0 0 0-7.1z" />
          </svg>
          {wishlistCount > 0 && <span className="c4nav-badge">{wishlistCount}</span>}
        </Link>

        <Link
          className="c4nav-mobilebar__icon"
          to={process.env.PUBLIC_URL + (isLoggedIn ? "/profile" : "/login-register")}
          aria-label={isLoggedIn ? "My account" : "Sign in or register"}
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="8" r="3.8" />
            <path d="M4.6 20.2a7.6 7.6 0 0 1 14.8 0" />
          </svg>
        </Link>
      </div>

      {/* ---------------- Desktop ---------------- */}
      <div className="c4nav-desktop">
        {/* Top black utility bar */}
        <div className="c4nav-top">
          <div className={containerClass}>
            <div className="c4nav-top__inner">
              <Link className="c4nav-logo" to={process.env.PUBLIC_URL + "/"}>
                <img src={NAV_LOGO} alt="CEY4HUB" />
              </Link>

              <div className="c4nav-deliver">
                <i className="pe-7s-map-2 c4nav-deliver__icon" />
                <span>
                  <span className="c4nav-deliver__label" style={{ display: "block" }}>
                    Deliver to
                  </span>
                  <span className="c4nav-deliver__value">London</span>
                </span>
              </div>

              <form className="c4nav-search" onSubmit={handleSearch} role="search">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search for Products, Categories and More"
                  aria-label="Search"
                />
                <button type="submit" aria-label="Search">
                  <i className="pe-7s-search" />
                </button>
              </form>

              <div className="c4nav-lang">
                <UkFlag />
                <span className="c4nav-lang__text">EN / £</span>
              </div>

              <div className="c4nav-actions header-right-wrap">
                {/* Cart (keeps the existing dropdown behavior) */}
                <div className="c4nav-cart-wrap same-style cart-wrap">
                  <button className="c4nav-icon icon-cart" onClick={toggleCart} aria-label="Cart">
                    <i className="pe-7s-cart" />
                    {cartCount > 0 && <span className="c4nav-badge">{cartCount}</span>}
                  </button>
                  <MenuCart userProfile={data?.user} />
                </div>

                {/* Wishlist */}
                <Link className="c4nav-icon" to={process.env.PUBLIC_URL + "/wishlist"} aria-label="Wishlist">
                  <i className="pe-7s-like" />
                  {wishlistCount > 0 && <span className="c4nav-badge">{wishlistCount}</span>}
                </Link>

                {/* Account */}
                {isLoggedIn ? (
                  <Link className="c4nav-account" to={process.env.PUBLIC_URL + "/profile"}>
                    <i className="pe-7s-user c4nav-account__icon" />
                    <span>
                      <span className="c4nav-account__label" style={{ display: "block" }}>
                        Welcome
                      </span>
                      <span className="c4nav-account__value">My Account</span>
                    </span>
                  </Link>
                ) : (
                  <Link className="c4nav-account" to={process.env.PUBLIC_URL + "/login-register"}>
                    <i className="pe-7s-user c4nav-account__icon" />
                    <span>
                      <span className="c4nav-account__label" style={{ display: "block" }}>
                        Welcome
                      </span>
                      <span className="c4nav-account__value">Sign in/Register</span>
                    </span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* White main nav bar */}
        {stuck && <div style={{ height: 57 }} />}
        <div ref={mainRef} className={clsx("c4nav-main", stuck && "stick")}>
          <div className={containerClass}>
            <div className="c4nav-main__inner">
              {/* All categories */}
              <div className={clsx("c4nav-cats", catsOpen && "is-open")}>
                <button
                  className="c4nav-cats__btn"
                  onClick={() => setCatsOpen((o) => !o)}
                  aria-expanded={catsOpen}
                >
                  <i className="pe-7s-menu" />
                  All Categories
                  <i className="pe-7s-angle-down" />
                </button>
                <div className="c4nav-cats__menu">
                  {CATEGORIES.map((cat) => (
                    <Link
                      key={cat}
                      to={process.env.PUBLIC_URL + "/shop-grid-standard"}
                      onClick={() => setCatsOpen(false)}
                    >
                      {cat}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Menu links */}
              <ul className="c4nav-menu">
                <li>
                  <NavLink
                    to={process.env.PUBLIC_URL + "/"}
                    end
                    className={({ isActive }) => (isActive ? "is-active" : "")}
                  >
                    Home
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to={process.env.PUBLIC_URL + "/shop-grid-standard"}
                    className={({ isActive }) => (isActive ? "is-active" : "")}
                  >
                    Shop
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to={process.env.PUBLIC_URL + "/shop-grid-standard"}
                    className={({ isActive }) => (isActive ? "is-active" : "")}
                  >
                    New Arrivals
                  </NavLink>
                </li>
                <li>
                  <span className="c4nav-menu__toggle">
                    Pages <i className="pe-7s-angle-down" />
                  </span>
                  <ul className="c4nav-sub">
                    <li><Link to={process.env.PUBLIC_URL + "/cart"}>Cart</Link></li>
                    <li><Link to={process.env.PUBLIC_URL + "/checkout"}>Checkout</Link></li>
                    <li><Link to={process.env.PUBLIC_URL + "/wishlist"}>Wishlist</Link></li>
                    <li><Link to={process.env.PUBLIC_URL + "/compare"}>Compare</Link></li>
                    <li><Link to={process.env.PUBLIC_URL + "/profile"}>My Account</Link></li>
                    <li><Link to={process.env.PUBLIC_URL + "/order-status"}>Order Status</Link></li>
                  </ul>
                </li>
                <li>
                  <NavLink
                    to={process.env.PUBLIC_URL + "/about"}
                    className={({ isActive }) => (isActive ? "is-active" : "")}
                  >
                    About
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to={process.env.PUBLIC_URL + "/contact"}
                    className={({ isActive }) => (isActive ? "is-active" : "")}
                  >
                    Contact
                  </NavLink>
                </li>
                {/* Collaboration entry hidden for now.
                <li>
                  <NavLink
                    to={process.env.PUBLIC_URL + "/collabaration"}
                    className={({ isActive }) => (isActive ? "is-active" : "")}
                  >
                    Collaboration
                  </NavLink>
                </li>
                */}
              </ul>

              {/* Utility: track order + phone */}
              <div className="c4nav-utility">
                <Link className="c4nav-track" to={process.env.PUBLIC_URL + "/order-status"}>
                  <i className="pe-7s-map-2" />
                  Track your order
                </Link>
                <a className="c4nav-phone" href="tel:+442033711782">
                  <i className="pe-7s-call" />
                  +44 (0) 20 3371 1782
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile navigation drawer */}
      <MobileMenu user={data?.user} />
    </header>
  );
};

HeaderOne.propTypes = {
  layout: PropTypes.string,
  top: PropTypes.string,
  borderStyle: PropTypes.string,
  headerPaddingClass: PropTypes.string,
  headerPositionClass: PropTypes.string,
  headerBgClass: PropTypes.string,
};

export default HeaderOne;
